<template>
  <div>
    <v-container
      grid-list-xl
      fluid
    >
      <v-layout
        row
        wrap
        color="transparent"
        class="mb-3"
      >
        <!-- Toolbar Navigation -->
        <v-toolbar
          flat
          color="transparent"
          class="mb-4 mt-4"
          height="20px"
        >
          <v-btn
            icon
            class="text--secondary"
            @click="goBack"
          >
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <span class="text-capitalize subheading text--secondary">
            {{ $t('Settings') }}
          </span>
          <v-spacer />
          <v-btn
            @click="setRightDrawer('Zone Form')"
          >
            {{ $t('Add new zone') }}
          </v-btn>
        </v-toolbar>
        <!-- Title -->
        <v-flex xs12>
          <h3 class="text-capitalize ml-2 font-weight-regular">
            {{ $t('Zone Settings') }}
          </h3>
          <v-divider mb-3 />
        </v-flex>
      </v-layout>

      <v-layout
        row
        wrap
      >
        <v-flex xs12>
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Countries Limitation') }}
              </h3>

              <v-flex class="pl-0 pt-1">
                <span
                  class="caption"
                >
                  {{ $t('List of countries that should be allowed for selection in various store places.') }}
                </span>
              </v-flex>
              <v-flex class="pl-0 pt-1">
                <span
                  class="caption"
                >
                  {{ $t('List of telephones prefixes that should be allowed for selection for phone numbers.') }}
                </span>
              </v-flex>

            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <v-combobox
                  v-if="settings"
                  :loading="isLoading && !settings"
                  :label="$t('Allowed Countries List')"
                  :items="countries"
                  v-model="settings.countries.allowed_list"
                  :return-object="false"
                  class="pa-2"
                  item-text="name"
                  item-value="code"
                  multiple
                  clearable
                >
                  <template v-slot:selection="{ item, parent, selected }">
                    <v-chip
                      :selected="selected"
                      label
                      small
                    >
                      {{ getCountryName(item) }}
                      <v-icon
                        small
                        @click="parent.selectItem(item)"
                      >close</v-icon>
                    </v-chip>
                  </template>
                </v-combobox>

                <v-combobox
                  v-if="settings"
                  :loading="isLoading && !settings"
                  :label="$t('Allowed Telephones Prefixes List')"
                  :items="countriesPrefixes"
                  v-model="settings.countries.allowed_telephones_list"
                  :return-object="false"
                  :item-text="(item) => getCountryWithPhone(item.code)"
                  class="pa-2"
                  item-value="code"
                  multiple
                  clearable
                >
                  <template v-slot:selection="{ item, parent, selected }">
                    <v-chip
                      :selected="selected"
                      label
                      small
                    >
                      {{ getCountryName(item) }}
                      <v-icon
                        small
                        @click="parent.selectItem(item)"
                      >close</v-icon>
                    </v-chip>
                  </template>
                </v-combobox>

                <v-btn @click="saveSettings">
                  {{ $t('Save') }}
                </v-btn>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout
        row
        wrap
      >
        <v-flex xs12>
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Region Settings') }}
              </h3>
              <span
                class="caption"
              >
                {{ $t('List of countries for which region requirement should be disabled and regions should be hidden.') }}
              </span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <v-combobox
                  v-if="settings"
                  :loading="isLoading && !settings"
                  :label="$t('Countries List')"
                  :items="countries"
                  v-model="settings.regions.ignore_requirement_for"
                  :return-object="false"
                  class="pa-2"
                  item-text="name"
                  item-value="code"
                  multiple
                  small-chips
                  clearable
                >
                  <template v-slot:selection="{ item, parent, selected }">
                    <v-chip
                      :selected="selected"
                      label
                      small
                    >
                      {{ getCountryName(item) }}
                      <v-icon
                        small
                        @click="parent.selectItem(item)"
                      >close</v-icon>
                    </v-chip>
                  </template>
                </v-combobox>

                <v-btn @click="saveSettings">
                  {{ $t('Save') }}
                </v-btn>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout
        row
        wrap
      >
        <v-flex xs12>
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              md4
              offset-md1
            >
              <h3 class="title font-weight-regular pt-3">
                {{ $t('Localisation') }}
              </h3>
              <span
                class="caption"
              >{{ $t('Add localisation i.e. country and territories that your store is able to send deliveries') }}</span>
            </v-flex>
            <v-flex
              xs12
              md6
            >
              <v-card
                flat
                class="pa-3"
              >
                <localisation-zone-list
                  :loading="isLoading"
                  :zones="zones"
                  @edit-zone="editZone"
                />
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <RightDrawer
      :open="rightDrawer"
      title="Zone Settings"
      @close="closeDrawer()"
    >
      <zone-form
        v-if="rightDrawer"
        :selected-zone-id="selectedZoneId"
        @close-drawer="closeDrawer"
      />
    </RightDrawer>
  </div>
</template>

<script>
import LoadingState from '@/components/LoadingState'
import ZoneService from '@/modules/Settings/service'
import LocalisationZoneList from '@/modules/Settings/components/Locatisation/LocalisationZoneList'
import ZoneForm from '@/modules/Settings/components/ZoneForm'
import RightDrawer from '@/components/RightDrawer'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SettingsZone',

  components: {
    LocalisationZoneList,
    LoadingState,
    ZoneForm,
    RightDrawer
  },

  data () {
    return {
      loading: false,
      rightDrawer: false,
      zones: [],
      selectedZoneId: '',
      isLoading: true,
      settings: null,
      ignoredCountries: []
    }
  },

  computed: {
    ...mapGetters(['countries']),

    countriesPrefixes () {
      return this.countries.filter(country => country.dialling.prefix)
    }
  },

  async created () {
    this.isLoading = true

    await this.setZones()
    if (this.countries.length === 0) {
      await this.initCountries()
    }
    this.settings = await this.$http.make('settings').find('localisation')

    this.isLoading = false
  },

  methods: {
    ...mapActions(['setCountries', 'setAllowedLists']),

    async initCountries () {
      const countries = await this.$http.make('country').get()
      this.setCountries(countries)
    },

    async setZones () {
      this.zones = await ZoneService.loadZones()
    },

    closeDrawer () {
      this.rightDrawer = false
      this.selectedZoneId = ''
      this.setZones()
    },

    goBack () {
      this.$router.push({ name: 'settings' })
    },

    setRightDrawer (title) {
      this.rightDrawer = true
    },

    async editZone (zone) {
      this.selectedZoneId = zone.id
      this.rightDrawer = true
    },

    getCountry (code) {
      return this.countries.find(country => country.code === code)
    },

    getCountryName (code) {
      const country = this.getCountry(code)
      return country ? country.name : code
    },

    getCountryWithPhone (code) {
      const country = this.getCountry(code)
      return country.name + ` (+${country.dialling.prefix})`
    },

    saveSettings () {
      let settings = this.$http.make('settings', {
        data: {
          regions: this.settings.regions,
          countries: this.settings.countries
        }
      })
      settings.code_url = 'localisation'

      try {
        settings.save()
        this.setAllowedLists(this.settings.countries)
        this.$toast.success(this.$t('Settings updated'))
      } catch (e) {
        this.$vuetify.goTo(0)
        this.$toast.error(this.$t(e.message))
      }
    }
  }
}
</script>
